import React from "react";

import DropzoneIcon from "@common/assets/images/dropzone-icon.png";
import { AssigneeDropdown } from "@common/components/Dropdown/AssigneeDropdown/AssigneeDropdown";
import { FeedbackTypeDropdown } from "@common/components/Dropdown/FeedbackTypeDropdown/FeedbackTypeDropdown";
import { PriorityDropdown } from "@common/components/Dropdown/PriorityDropdown/PriorityDropdown";
import { StatusDropdown } from "@common/components/Dropdown/StatusDropdown/StatusDropdown";
import EditableTitle from "@common/components/Input/EditableTitle/EditableTitle";
import InputWithTags from "@common/components/Input/InputWithTags/InputWithTags";
import MessageInput from "@common/components/Input/MessageInput/MessageInput";
import Paragraph from "@common/components/Paragraph/Paragraph";
import displayToast from "@common/components/Toast/displayToast";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { t } from "@common/util/translator";
import AttachmentsContainer from "@main/pages/shared/components/Attachments/AttachmentsContainer";
import {
  BasicsItem,
  getBasicsItemIcon,
} from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/BasicsItem/BasicsItem";
import { useUserFeedbackAddNewTicketView } from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/useUserFeedbackAddNewTicketView";
import * as Styled from "main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/UserFeedbackAddNewTicketView.styles";
import Dropzone from "react-dropzone";

type UseUserFeedbackAddTicketViewProps = ReturnType<typeof useUserFeedbackAddNewTicketView>;

export const UserFeedbackAddNewTicketView = (props: UseUserFeedbackAddTicketViewProps) => {
  return (
    <Dropzone
      onDrop={(acceptedFiles, fileRejections) => {
        if (acceptedFiles.length > 0) {
          acceptedFiles.forEach((file) => {
            props.addAttachment(file as File);
          });
        }

        fileRejections.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            if (error.code === "file-too-large") {
              displayToast({
                content: `File "${rejection.file.name}" is too big! Max size is 10MB.`,
              });
            } else if (error.code === "file-invalid-type") {
              displayToast({
                content: `Whoopsie, that doesn't look like a valid file format`,
              });
            }
          });
        });
      }}
      maxSize={10 * 1024 * 1024} // 10MB
      noKeyboard={true}
      noClick={true}
    >
      {({ getRootProps, getInputProps, isDragActive, open: openFilePicker }) => (
        <Flex
          style={{ position: "relative", height: "100%" }}
          $column
          $gap={2.5}
        >
          <input {...getInputProps()} />
          <Flex
            {...getRootProps()}
            style={{ height: "100%" }}
            $gap={1.2}
          >
            <Styled.MainContainerCard>
              <Styled.ContainerScroll
                $column
                $gap={2.4}
              >
                <EditableTitle
                  value={props.title}
                  onChangeEvent={(newTitle: string) => props.setTitle(newTitle)}
                  maxLength={10000}
                  placeholder={"Ticket description ..."}
                  autoFocus
                  focusTrigger={props.resetCount}
                />
                <Flex $gap={1}>
                  <FeedbackTypeDropdown
                    feedbackType={props.feedbackType}
                    isOpened={props.isFeedbackTypeDropdownOpened}
                    setIsOpened={props.setIsFeedbackTypeDropdownOpened}
                    changeFeedbackType={props.onFeedbackTypeSelected}
                  />
                  <AssigneeDropdown
                    assigneeId={props.assigneeId}
                    changeAssignee={props.setAssigneeId}
                  />
                  <StatusDropdown
                    status={props.status}
                    isOpened={props.isStatusDropdownOpened}
                    setIsOpened={props.setIsStatusDropdownOpened}
                    changeStatus={props.setStatus}
                  />
                  <PriorityDropdown
                    priority={props.priority}
                    isOpened={props.isPriorityDropdownOpened}
                    setIsOpened={props.setIsPriorityDropdownOpened}
                    changePriority={props.setPriority}
                  />
                </Flex>
                <InputWithTags
                  commonTags={props.commonTags}
                  selectedTags={props.tags}
                  onAddTag={(tag) => props.setTags([...props.tags, tag])}
                  onRemoveTag={(tag) => props.setTags(props.tags.filter((t) => t.name !== tag))}
                />
                <AttachmentsContainer
                  attachments={props.attachments}
                  onSelectFileClick={openFilePicker}
                  onRemoveClick={props.removeAttachment}
                />
                <MessageInput
                  autoFocus={false}
                  value={props.comment}
                  onChange={props.setComment}
                  onCommonCommentClick={props.setCommonComment}
                  onCommonCommentSettingsClick={props.showCommonCommentsSettings}
                  placeholder={t("userFeedbackDetailsPage.chat.notePlaceholder")}
                  withoutSendButton={true}
                  maxLength={10000}
                />
              </Styled.ContainerScroll>
            </Styled.MainContainerCard>
            <Styled.BasicsContainerCard>
              <Paragraph
                style={{ marginBottom: "1.2rem", marginLeft: "0.8rem" }}
                p3
              >
                {"Basics"}
              </Paragraph>
              {props.basics.map((item) => (
                <BasicsItem
                  key={item.key}
                  icon={getBasicsItemIcon(item.key)}
                  onValueSelected={props.onBasicsValueChanged}
                  enabled={item.key !== "os_android" && item.key !== "os_ios"}
                  basicsItem={item}
                />
              ))}
            </Styled.BasicsContainerCard>
          </Flex>

          {/* Drop overlay */}
          {isDragActive && <DropOverlay />}
        </Flex>
      )}
    </Dropzone>
  );
};

const DropOverlay = () => {
  return (
    <Styled.DropOverlay>
      <img
        alt={"Dropzone icon"}
        width={64}
        height={64}
        src={DropzoneIcon}
      />
      <Paragraph
        p3
        color={vars.colors.orange}
      >
        {"Just drop to upload"}
      </Paragraph>
    </Styled.DropOverlay>
  );
};
